'use client';

import { ArrowDown, ArrowUp, ArrowUpDown, Columns3 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ActionMenu } from '@platform/ui/components/action-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@platform/ui/components/table';
import { useMediaQuery } from '@platform/ui/hooks/use-media-query';
import { cn } from '@platform/ui/lib/utils';

export interface Column<Row> {
    key: string;
    header: ReactNode;
    /** Renders the cell. Given the row rather than a value so a cell can combine fields. */
    cell: (row: Row) => ReactNode;
    /** Right-aligns a numeric column, where digits line up under each other. */
    align?: 'start' | 'end';
    /** Collapses the column to its content instead of sharing the leftover width. */
    narrow?: boolean;
    /**
     * Lets the header sort the list, and says which way a first press sorts:
     * 'desc' for a date, where the newest is what a person is after, 'asc' for
     * a name or a code (docs/11 §4). The table only reports the choice; the
     * rows arrive sorted, from the server.
     */
    sortable?: SortDirection;
    /**
     * Marks a column an operation needs now and then rather than always — an
     * image, a barcode, which package carries a line (docs/11 §4). It starts
     * hidden and the table's column picker turns it on. `header` is what the
     * picker calls it, so it has to read as a name on its own.
     */
    optional?: boolean;
}

export type SortDirection = 'asc' | 'desc';

/** Which column the rows are ordered by, and which way. */
export interface SortState {
    key: string;
    direction: SortDirection;
}

export interface DataTableProps<Row> {
    columns: Array<Column<Row>>;
    rows: Row[];
    rowKey: (row: Row) => string;
    /** Makes a row open on click and on Enter. */
    onRowClick?: (row: Row) => void;
    /** Shown in place of the rows when there are none. */
    empty?: ReactNode;
    /**
     * What the list says when its rows could not be read: the failure and a
     * way to ask again. It stands where the rows go, and `empty` is never drawn
     * beside it, so a list that failed does not read as an empty one; rows an
     * earlier read left stay under it (docs/11 §4).
     */
    error?: ReactNode;
    /** Dims the rows while a refetch is in flight, keeping the old ones visible. */
    busy?: boolean;
    /** The order the rows are in, drawn on the header of the column that set it. */
    sort?: SortState;
    /** Called when a sortable header is pressed; the list asks for its rows again. */
    onSortChange?: (sort: SortState) => void;
    caption?: string;
    /**
     * On a narrow screen a wide table is a scroll nobody finishes. When given,
     * each row renders as a card below `cardBreakpoint`: the few facts a person
     * needs, still one tap into the record.
     */
    card?: (row: Row) => ReactNode;
    cardBreakpoint?: string;
    /**
     * The accessible name of the column picker, which is drawn only when a
     * column is `optional`. Without it the optional columns stay hidden and
     * nothing offers them, which is a table nobody can finish reading.
     */
    columnsLabel?: string;
    /**
     * Where this table remembers which optional columns are on. A convenience
     * of this browser, like the theme and the language — never a setting, so
     * nothing is stored for the seller. Omitted, the choice lasts the visit.
     */
    columnsKey?: string;
}

/**
 * A record list (docs/11 §4). On a narrow screen the rows become cards in a
 * list named by the caption, as the table is named by it on a wide one.
 */
export function DataTable<Row>({
    columns,
    rows,
    rowKey,
    onRowClick,
    empty,
    error,
    busy,
    sort,
    onSortChange,
    caption,
    card,
    cardBreakpoint = '(max-width: 40rem)',
    columnsLabel,
    columnsKey,
}: DataTableProps<Row>) {
    const narrow = useMediaQuery(cardBreakpoint);
    const optional = columns.filter((column) => column.optional);
    const [shown, setShown] = useState<string[]>(() => storedColumns(columnsKey));
    const picker = columnsLabel !== undefined && optional.length > 0;
    // Hidden unless the picker exists to turn them back on: a column nobody
    // can reach is a column that is simply missing.
    const visible = picker ? columns.filter((column) => !column.optional || shown.includes(column.key)) : columns;

    function toggleColumn(key: string) {
        setShown((current) => {
            const next = current.includes(key) ? current.filter((candidate) => candidate !== key) : [...current, key];
            storeColumns(columnsKey, next);
            return next;
        });
    }

    // A read that failed answers no rows, and an empty state drawn for it says
    // there is nothing to list: Ürünler showed "Henüz Ürün Yok" and its Ürün
    // Ekle beside the failure, over a catalogue of hundreds (EPZ-0290).
    if (rows.length === 0 && error) {
        return <>{error}</>;
    }
    if (rows.length === 0 && empty) {
        return <div className="p-4">{empty}</div>;
    }

    // A refetch keeps the previous rows on screen and dims them: blanking the list
    // on every keystroke of a search box is far more disorienting than a moment of
    // slightly stale rows.
    const dimmed = cn('transition-opacity', busy && 'opacity-60');

    // Rows an earlier read left stay, under the notice that the latest one
    // failed: they are still the best the list has. The notice keeps its
    // distance from them whatever holds the table.
    const framed = (list: ReactNode) =>
        error ? (
            <div className="flex flex-col gap-4">
                {error}
                <div>{list}</div>
            </div>
        ) : (
            list
        );

    if (card && narrow) {
        return framed(
            <ul className={cn('flex flex-col', dimmed)} aria-label={caption}>
                {rows.map((row) => (
                    <li key={rowKey(row)} className="border-t border-border first:border-t-0">
                        {onRowClick ? (
                            <button
                                type="button"
                                onClick={() => onRowClick(row)}
                                className="block w-full px-2 py-3 text-start hover:bg-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                            >
                                {card(row)}
                            </button>
                        ) : (
                            <div className="px-2 py-3">{card(row)}</div>
                        )}
                    </li>
                ))}
            </ul>,
        );
    }

    return framed(
        <>
            {/* Above the table and at its end, where it is out of the way of the
                first column a person reads. */}
            {picker && (
                <div className="mb-2 flex justify-end">
                    <ActionMenu
                        size="sm"
                        icon={Columns3}
                        label={columnsLabel}
                        items={optional.map((column) => ({
                            label: typeof column.header === 'string' ? column.header : column.key,
                            checked: shown.includes(column.key),
                            onCheckedChange: () => toggleColumn(column.key),
                        }))}
                    />
                </div>
            )}
            <Table className={dimmed}>
                {caption && <caption className="sr-only">{caption}</caption>}
                <TableHeader>
                    <TableRow>
                        {visible.map((column) => {
                            const first = onSortChange ? column.sortable : undefined;
                            const current = sort?.key === column.key ? sort.direction : undefined;
                            const Arrow = current === 'asc' ? ArrowUp : current === 'desc' ? ArrowDown : ArrowUpDown;
                            return (
                                <TableHead
                                    key={column.key}
                                    className={cn(column.align === 'end' && 'text-end', column.narrow && 'w-px')}
                                    aria-sort={first ? (current === 'asc' ? 'ascending' : current === 'desc' ? 'descending' : 'none') : undefined}
                                >
                                    {first && onSortChange ? (
                                        // Every sortable header shows an arrow, so what can be
                                        // sorted is visible before anything is pressed.
                                        <button
                                            type="button"
                                            onClick={() =>
                                                onSortChange({ key: column.key, direction: current ? (current === 'asc' ? 'desc' : 'asc') : first })
                                            }
                                            className={cn(
                                                'inline-flex items-center gap-1 rounded-badge uppercase hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                                                current && 'text-foreground',
                                            )}
                                        >
                                            {column.header}
                                            <Arrow aria-hidden="true" className={cn('size-3.5 shrink-0', !current && 'opacity-50')} />
                                        </button>
                                    ) : (
                                        column.header
                                    )}
                                </TableHead>
                            );
                        })}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={rowKey(row)}
                            className={cn(
                                onRowClick &&
                                    'cursor-pointer hover:bg-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                            )}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            // A clickable row has to be reachable from the keyboard, or the only way
                            // into a record is a mouse.
                            tabIndex={onRowClick ? 0 : undefined}
                            onKeyDown={
                                onRowClick
                                    ? (event) => {
                                          if (event.key === 'Enter') onRowClick(row);
                                      }
                                    : undefined
                            }
                        >
                            {visible.map((column) => (
                                <TableCell
                                    key={column.key}
                                    className={cn(column.align === 'end' && 'text-end tabular-nums', column.narrow && 'w-px whitespace-nowrap')}
                                >
                                    {column.cell(row)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>,
    );
}

/**
 * Which optional columns this browser last had on for a table.
 *
 * Read and written the way the theme is (lib/theme.ts): private browsing can
 * refuse storage, and a table that throws rather than draws is worse than one
 * that forgets.
 */
function columnsStorageKey(key: string): string {
    return `table-columns:${key}`;
}

function storedColumns(key: string | undefined): string[] {
    if (!key) return [];
    try {
        const stored = localStorage.getItem(columnsStorageKey(key));
        if (!stored) return [];
        const parsed: unknown = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
    } catch {
        return [];
    }
}

function storeColumns(key: string | undefined, columns: string[]): void {
    if (!key) return;
    try {
        localStorage.setItem(columnsStorageKey(key), JSON.stringify(columns));
    } catch {
        // The choice simply will not outlast the visit.
    }
}

/** One line of a phone card: its two ends apart, the text free to truncate. */
export function TableCardRow({ className, children }: { className?: string; children: ReactNode }) {
    return <div className={cn('flex min-w-0 items-center justify-between gap-3 not-first:mt-1', className)}>{children}</div>;
}

/** The record's name on a phone card, cut to one line. */
export function TableCardTitle({ className, children }: { className?: string; children: ReactNode }) {
    return <span className={cn('min-w-0 truncate font-medium', className)}>{children}</span>;
}

/** The quieter facts on a phone card: a date, a channel, a count. Short of room, they wrap whole. */
export function TableCardMeta({ className, children }: { className?: string; children: ReactNode }) {
    return <span className={cn('flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-13 text-muted-foreground', className)}>{children}</span>;
}
